// @djsem/render-state — Venue / Enterprise Rights & Safety (Prompt 26R).
// Rights + safety confirmation layer OVER the tested 12G VenueProfileService: commercial
// export confirmations, simulation-only warnings, and laser safety boundaries. Never
// clears rights; never claims physical laser safety (hard rule — simulation only).

import { VenueProfileService, type DJSEMVenueProfile } from "./venueProfile";
import { SeqCounter } from "@djsem/shared";
import { type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult } from "@djsem/shared";

/** Result of requesting confirmation to commercially export a show pack. */
export interface DJSEMCommercialExportConfirmation {
  confirmationId: string;
  showPackId: string;
  venueProfileId?: string;
  confirmedBy?: string;
  confirmedAt?: string;
  acknowledgedWarnings: string[];
  outstandingWarnings: string[];
  confirmed: boolean;
}

/** Input for {@link VenueRightsSafetyService.requestCommercialExportConfirmation}. */
export interface RequestCommercialExportInput {
  showPackId: string;
  venueProfile?: DJSEMVenueProfile;
  rights: { hasRestricted: boolean; hasUnknown: boolean };
  usesLaserSimulation?: boolean;
  usesStrobeSimulation?: boolean;
  acknowledgedWarnings?: string[];
  confirmedBy?: string;
  confirmedAt?: string;
}

const _seq = new SeqCounter();

/**
 * Rights and safety confirmation layer over the tested venue profile service: commercial
 * export confirmations, simulation-only warnings, and laser safety boundaries. Never
 * clears rights and never claims physical laser safety — simulation only.
 */
export class VenueRightsSafetyService {
  private venues = new VenueProfileService();

  /**
   * Builds the warning set a commercial export must acknowledge. Restricted rights are a
   * hard block (cannot be acknowledged away); everything else is confirm-to-proceed.
   * @param input - the show pack id, rights summary, simulation-usage flags, and any
   *   already-acknowledged warnings.
   * @returns the confirmation result: outstanding warnings and whether it's confirmed.
   */
  requestCommercialExportConfirmation(input: RequestCommercialExportInput): DJSEMCommercialExportConfirmation {
    const warnings: string[] = [];
    const blockers: string[] = [];
    if (input.rights.hasRestricted) blockers.push("BLOCK: pack contains restricted-rights assets; commercial export not possible.");
    if (input.rights.hasUnknown) warnings.push("Pack contains unknown-rights assets; you are responsible for clearance.");
    if (input.usesLaserSimulation) {
      warnings.push("Laser content is SIMULATION ONLY. DJSEM outputs no laser hardware control and makes no physical laser-safety claims.");
      if (input.venueProfile && !input.venueProfile.safetyProfile.allowLaserSimulation) warnings.push(`Venue ${input.venueProfile.id} does not permit laser simulation.`);
    }
    if (input.usesStrobeSimulation) warnings.push("Strobe content is simulation only; photosensitivity considerations are the venue operator's responsibility.");
    if (input.venueProfile?.safetyProfile.requiresOperatorReview) warnings.push(`Venue ${input.venueProfile.id} requires operator review before show use.`);

    const ack = new Set(input.acknowledgedWarnings ?? []);
    const outstanding = [...blockers, ...warnings.filter((w) => !ack.has(w))];
    const confirmed = blockers.length === 0 && outstanding.length === 0;
    return {
      confirmationId: `cec_${_seq.next()}`,
      showPackId: input.showPackId,
      venueProfileId: input.venueProfile?.id,
      confirmedBy: confirmed ? input.confirmedBy : undefined,
      confirmedAt: confirmed ? input.confirmedAt : undefined,
      acknowledgedWarnings: [...ack],
      outstandingWarnings: outstanding,
      confirmed,
    };
  }

  /**
   * Validates a venue profile's structural checks plus the safety-boundary rule that no
   * profile text may claim real laser output or physical laser-safety certification.
   * @param profile - the venue profile to validate.
   * @returns a validation result listing any structural or safety-claim issues.
   */
  validateVenueSafetyBoundaries(profile: DJSEMVenueProfile): DJSEMValidationResult {
    // Delegate structural checks to 12G, then add the 26R boundary checks.
    const base = this.venues.validateVenueProfile(profile);
    const issues: ValidationResult["issues"] = base.ok ? [] : [...base.issues];
    // No profile may CLAIM real laser output or physical safety certification. Denials
    // ("no real laser output", "not real laser") are the point of the notes — allowed.
    const flat = JSON.stringify(profile).toLowerCase()
      .replace(/\b(no|not|never|without)\s+(any\s+)?real laser[^."]*/g, "")
      .replace(/\bno ilda output[^."]*/g, "");
    if (/(real laser|ilda output|laser certified|audience scanning)/.test(flat))
      issues.push({ path: "$", message: "profile text claims real laser output/safety — not allowed" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "venue_safety_boundary");
  }
}
